/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/**
 * The strings used in labels.
 * @export
 */
export const CONFIGURATION_STRINGS = {
  add: 'Add',
  addAttribute: 'Add attribute',
  addDam: 'Add DAM',
  addDescriptiveName: 'Add a descriptive name',
  addDomain: 'Add domain',
  addRoute: 'Add route',
  addVariable: 'Add variable',
  and: 'And',
  apiError: `There was a problem completing your request. Please try again later.`,
  apiEventTransformerDescription:
    'Use the Event transformer to define variables that use JSON path to reference values in the original event source. The transformed event is sent to a target instead of the original event. If you do not define variables, the entirety of the event will be sent to the target.',
  apiKey: 'API key',
  apiKeyDetails: 'API key details',
  apiTargetType: 'API',
  attributeFiltersDescription:
    'Filter events sent from your source to your target. Events matching all defined attributes are sent to your selected target.',
  attributeFiltersOptional: 'Attribute filters (optional)',
  attributeRequired: 'Attribute*',
  auConnectWebhookDomain: 'in.au.connect.squiz.cloud/hook',
  cancel: 'Cancel',
  cancelJob: 'Cancel job',
  cancelled: 'Cancelled',
  changesCouldNotBeSaved: 'Changes could not be saved',
  changesSaved: 'Changes saved',
  configuration: 'Configuration',
  context: 'Context',
  contexts: 'Contexts',
  contextSuccessToastDescription1: 'You have successfully created ',
  contextSuccessToastDescription2: ' context.',
  create: 'Create',
  createAContext: 'Create a context',
  createContext: 'Create context',
  createdContextTitle: 'Context created',
  createEventSubscription: 'Create event subscription',
  currentDamIntegrations: 'Current DAM integrations',
  damIntegrationAdded: 'DAM integration added',
  damIntegrationAddedDescription: 'You have successfully added the DAM integration',
  damIntegrationListDescription:
    'These are the digital asset management integrations configured for use in your system.',
  damIntegrations: 'DAM integrations',
  damIntegrationsAccessTokenUrl: 'Access token URL',
  damIntegrationsAccessTokenUrlDescription: 'Used to verify a user’s right to access the integration',
  damIntegrationsAuthRedirectURI:
    'The authorization redirect URI will need to be whitelisted in your integration settings before the DAM can be enabled.',
  damIntegrationsAuthRedirectURISection: 'Authorization redirect URI',
  damIntegrationsAuthUrl: 'Auth URL',
  damIntegrationsAuthUrlDescription: 'The login URL for the DAM',
  damIntegrationsClientId: 'Client ID',
  damIntegrationsClientIdDescription: 'An alpha numeric string provided by your DAM provider',
  damIntegrationsClientSecret: 'Client secret',
  damIntegrationsClientSecretDescription: 'Secret string used by the integration to authenticate its identity',
  damIntegrationsConfigurationSection: 'Configuration settings',
  damIntegrationsConnectionTest: 'Test connection',
  damIntegrationsConnectionTestDescription: 'Test whether the DAM render settings return a valid connection.',
  damIntegrationsCopyUrl: 'Authorization redirect URI',
  damIntegrationsCopyUrlDescription: '',
  damIntegrationsDamDomain: 'DAM domain',
  damIntegrationsDamDomainDescription: 'The root domain for the DAM',
  damIntegrationsDescription: 'Manage configuration settings for third-party DAM integrations',
  damIntegrationsDetailsSection: 'Details',
  damIntegrationsEnabled: 'Enable this integration',
  damIntegrationsEnabledDescription: 'This will enable access to this integration for users on your site.',
  damIntegrationsEnabledSection: 'Enable integration',
  damIntegrationsM2MClientId: 'Machine to machine client ID',
  damIntegrationsM2MClientIdDescription: 'An alpha numeric string provided by your DAM provider',
  damIntegrationsM2MClientSecret: 'Machine to machine client secret',
  damIntegrationsM2MClientSecretDescription: 'Secret string used by the integration to authenticate its identity',
  damIntegrationsName: 'Friendly name',
  damIntegrationsNameDescription: 'The name that will appear to users',
  damIntegrationsRenderSection: 'Render settings',
  damIntegrationsScope: 'Scope',
  damIntegrationsScopeDescription: 'Define what the integration can do on the user’s behalf',
  damIntegrationsScopeInvalidFormat: 'Invalid scope format, should be either {scope-name}:read or {scope-name}:write',
  damIntegrationsTitle: 'DAM integrations',
  damIntegrationsType: 'Integration type',
  damIntegrationsTypeDescription: 'The type of the integration to be setup for this DAM',
  dashboard: 'Dashboard',
  datetimePickerPlaceholder: 'Select a date',
  delete: 'Delete',
  deleted: 'Deleted',
  deleteEventSubscription: 'Delete event subscription',
  deleteEventSubscriptionDialogContent: 'If you delete this event subscription, your content will not be retrievable.',
  deleteEventSubscriptionDialogError: 'Failed to delete event subscription',
  deleteFilter: 'Delete filter',
  deleteRoute: 'Delete route',
  deleteRouteDialogText: 'Are you sure you want to delete this route?',
  description: 'Description',
  disabled: 'Disabled',
  domainEditNote: 'The domain can be edited under Configuration/Service settings.',
  domainSettings: 'Domain settings',
  duplicateEnvVarName: `This variable name is already in use`,
  edit: 'Edit',
  editDomain: 'Edit domain',
  editRoute: 'Edit route',
  enabled: 'Enabled',
  enterDescription: 'Enter description',
  enterName: 'Enter name',
  enterValue: 'Enter value',
  environmentVariables: 'Environment variables',
  error: 'Error',
  errorMessageWhenJobOrContextFetchFails: 'There was a problem loading this section. ',
  errorUnknown: `Something has gone wrong.`,
  eventPathsMap: 'Event paths map',
  eventPathsMapPlaceholder: `{\n  "info": "$.detail.info",\n  "state": "$.detail.state"\n}`,
  eventService: 'Event service',
  eventServiceDescription: 'Configure real-time updates for specific events or activities on all digital platforms.',
  eventSubscriptionActionMenuAriaLabel: 'Event subscription item menu button',
  eventSubscriptionCreated: 'Event subscription created',
  eventSubscriptionCreatedFailToast1: 'The event subscription ',
  eventSubscriptionCreatedFailToast2: ' could not be created.',
  eventSubscriptionCreatedSuccessToast1: 'You have successfully created ',
  eventSubscriptionCreatedSuccessToast2: ' event subscription.',
  eventSubscriptionDeleted: 'Event subscription deleted',
  eventSubscriptionDeletedFailToast1: 'The event subscription ',
  eventSubscriptionDeletedFailToast2: ' could not be deleted.',
  eventSubscriptionDeletedSuccessToast1: 'You have successfully deleted ',
  eventSubscriptionDeletedSuccessToast2: ' event subscription.',
  eventSubscriptions: 'Event subscriptions',
  eventTemplate: 'Event template',
  eventTemplatePlaceholder: `{\n  "message": "info <info> is in <state>"\n }`,
  eventTransformerOptional: 'Event transformer (optional)',
  eventTypeNote: 'Choose an event type that corresponds with the chosen source',
  eventTypeRequired: 'Event type*',
  executionId: 'Execution ID',
  fetchingRepositoryPaths: 'Fetching repository path...',
  finished: 'Finished',
  flowInput: 'Integrations flow input',
  flowInputDescription:
    'Use the Integrations flow input to define a JSON object which will be sent to the Integrations flow’s webhook as the POST request body when the schedule is triggered.',
  flowInputPlaceholder:
    '{\n  "employees": [\n    {\n      "firstName": "John",\n      "lastName": "Doe"\n    },\n    {\n      "firstName": "Anna",\n      "lastName": "Smith"\n    },\n    {\n      "firstName": "Peter",\n      "lastName": "Jones"\n    }\n  ]\n}',
  flowWebhookUrlRequired: 'Flow Webhook URL*',
  friendlyName: 'Friendly name',
  globalSettings: 'Global settings',
  helperTextForContextName: 'Enter a unique name',
  id: 'ID',
  incorrectFormatCriteria: 'The value does not match the format criteria',
  integrationsDetails: 'Integrations flow details',
  integrationsDetailsAlertMessage:
    'When an event matches the subscription details, it will start an execution of the active Integrations flow.',
  integrationsDetailsScheduledAlertMessage:
    'At the scheduled time for this event subscription, an execution of the active Integrations flow will start.',
  integrationsEventTransformerDescription:
    'Use the Event transformer to define variables that use JSON path to reference values in the original event source. The transformed event is sent to the Integrations flow’s webhook as the POST request body. If you do not define variables, the entirety of the event will be sent to the target.',
  integrationsFlowTargetType: 'Integrations flow',
  integrationsFlowTargetUrlHelperText: 'Enter the value in the format of an Integrations flow webhook URL',
  integrationType: 'Integration type',
  invalidEnvVarName: `Enter letters, numbers, dashes, periods, and underscores only`,
  invalidPathUrl: `The path URL must start with '/'`,
  invalidUrlError: 'This value is not a valid URL',
  invalidUrlForApiTargetTypeError: 'This field does not accept Integrations webhook URLs',
  invalidUrlForIntegrationsFlowTargetTypeError: 'The value does not match the format criteria',
  jobCancelFailToastDescription2: 'could not be cancelled.',
  jobCancelSuccessToastDescription1: 'You have successfully cancelled',
  jobCancelSuccessToastDescription2: 'job.',
  jobCancelSuccessToastTitle: 'Job cancelled',
  jobDetails: 'Job details',
  jobDetailsAlertMessage:
    'When an event occurs that matches the subscription details, it will start an execution of your selected job.',
  jobDetailsScheduledAlertMessage:
    'At the scheduled time for this event subscription, an execution of your selected job will be queued.',
  jobEventTransformerDescription:
    'Use the Event transformer to define variables that use JSON path to map values from the original event source to fields defined in the job schema. The transformed event is sent to Job runner as the input for the selected job.',
  jobExecution: 'Job Execution',
  jobFailToastDescription2: 'could not be started.',
  jobInputs: 'Job inputs',
  jobInputsDescription:
    'Use the job inputs form to set the values of the inputs that will be sent to the selected job when the schedule is triggered.',
  jobRunner: 'Job runner',
  jobRunnerDescription: 'Write custom scripts and configure when they are executed.',
  jobs: 'Jobs',
  jobsSearchPlaceholder: 'Search jobs by name and description',
  jobStartOrCancelFailToastDescription1: 'The job',
  jobSuccessToastDescription1: 'You have successfully started',
  jobSuccessToastDescription2: 'job.',
  jobSuccessToastTitle: 'Job started',
  jobTargetType: 'Job',
  jsonFormatNote: 'Enter the value in the format of a JSON object',
  jsonObjectInput: 'JSON object input',
  lastRun: 'Last run',
  latestVersion: 'Latest version',
  manageRouting: 'Manage routing',
  mappingActions: 'Mapping actions',
  name: 'Name',
  nameAlreadyInUseError: 'This name is already in use',
  nameRequired: 'Name*',
  noAvailableJobExecutions: 'No jobs have been started yet.',
  noContextsFound: 'No contexts found.',
  noDamIntegrations: 'There are no current DAM integrations.',
  noDomains: 'There are no domains set up for static routing',
  noEntriesFound: 'No entries found.',
  noEventSubscriptionsCreated: 'No event subscriptions have been created yet.',
  noJobContextsCreated: 'There are no contexts to show yet.',
  noJobsAvailable: 'There are no jobs to show yet.',
  noJobsFound: 'No jobs found.',
  noMatchingEventSubscriptions: 'There are no matching event subscriptions.',
  noMatchingJobContexts: 'There are no matching contexts.',
  noMatchingJobs: 'There are no matching jobs.',
  noRoutes: 'No routes found',
  pathUrl: 'Path URL',
  pathUrlNote: 'Optional path URL',
  queued: 'Queued',
  reasonForTerminatingJobExecution: 'Job was manually terminated',
  repositoryPath: 'Repository path',
  requiredField: `This field is required`,
  routeCreated: `Route created`,
  routeDeleted: `Route deleted`,
  routeUpdated: `Route updated`,
  ruleMatchingLogic: 'Rule matching logic',
  ruleRequired: 'Rule*',
  running: 'running',
  save: 'Save',
  saveChanges: 'Save changes',
  scheduler: 'Scheduler',
  schedulerOnceDescription: 'Schedule your event subscription to run on a specific date and time.',
  schedulerRecurringDescription: 'Schedule your event subscription to automatically run on specific dates and times.',
  schedulerType: 'Scheduler type',
  searchContextsPlaceholder: 'Search contexts',
  searchPlaceholder: 'Search event subscriptions',
  selectContext: 'Select context',
  selectRepositoryPath: 'Select repository path',
  serviceSettings: 'Service settings',
  serviceSettingsDescription: 'Manage organization-specific configurations and setting schemas for your DXP services.',
  setUpDomain: 'Set up a domain for static routing.',
  siteUrl: 'Site URL',
  sourceNote: 'This is where your event data comes from',
  sourceRequired: 'Source*',
  specifyInputValues: 'Specify input values',
  started: 'Started',
  startJob: 'Start job',
  startJobContextLabel: 'Context*',
  startJobDialogTitle: 'To start this job, select a context and enter values for the existing keys.',
  staticDomainConfig: 'Static domain config',
  staticRouting: 'Static routing',
  staticRoutingDescription: 'Set up custom URLs for files or folders that are managed in File store.',
  staticSites: 'Static sites',
  staticSitesListDescription: 'These are the static sites configured for use in your system.',
  staticSiteURL: 'Static site URL',
  status: 'Status',
  subscriptionDetails: 'Subscription details',
  subscriptionNameValidation: 'Enter letters, numbers, dashes, periods, underscores, and spaces only',
  subscriptions: 'Subscriptions',
  targetDescription:
    'A target is a destination that Event service sends an event to when it matches the subscription details.',
  targetDescriptionScheduled:
    'A target is a destination that Event service sends an event to at the scheduled time for this event subscription.',
  targetDetails: 'Target details',
  terminated: 'terminated',
  tryAgain: 'Try again',
  typeRequired: 'Type*',
  urlRequired: 'URL*',
  usConnectWebhookDomain: 'in.connect.squiz.cloud/hook',
  utcAlertMessage: 'Event scheduler subscriptions run on Coordinated Universal Time (UTC).',
  utcDateAndTimeRequired: 'UTC date and time*',
  value: 'Value',
  valueRequired: 'Value*',
  variableName: 'Variable Name',
  version: 'Version',
};
